// src/pages/RoomListPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import { Alert, Button, Image, Modal, Popover, Select, Table } from "antd";
import { QrcodeOutlined } from "@ant-design/icons";
import {
  ACTION_ICONS,
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  fmtMoney,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import RoomDetailDrawer from "../components/RoomDetailDrawer";
import RoomEditModal from "../components/RoomEditModal";
import AddRoomModal from "../components/AddRoomModal";
import notify from "../utils/notify";

const { confirm } = Modal;

const RoomListPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [setupLoading, setSetupLoading] = useState(true);
  const [setupError, setSetupError] = useState(null);
  const [setupStatus, setSetupStatus] = useState({
    hasRoomTypes: false,
    hasPrices: false,
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // VIEW / EDIT state
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);

  const [roomTypes, setRoomTypes] = useState([]);

  // Clear currentRoom ONLY when nothing is open
  useEffect(() => {
    if (!viewDrawerOpen && !editModalOpen) {
      setCurrentRoom(null);
    }
  }, [viewDrawerOpen, editModalOpen]);

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId) return;
    try {
      const res = await axiosInstance.get(`/RoomTypes/GetRoomTypes/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRoomTypes(list.filter((rt) => (rt?.roomTypeId ?? 0) > 0));
    } catch (error) {
      console.error("Error fetching room types:", error);
      setRoomTypes([]);
    }
  }, [hotelId]);

  const fetchPrices = useCallback(async () => {
    if (!hotelId) return [];
    try {
      const res = await axiosInstance.get(`/Price/hotel/${hotelId}`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error("Error fetching prices:", error);
      return [];
    }
  }, [hotelId]);

  const checkPrerequisites = useCallback(async () => {
    if (!hotelId) return;

    setSetupLoading(true);
    setSetupError(null);

    try {
      const roomTypesRes = await axiosInstance.get(
        `/RoomTypes/GetRoomTypes/${hotelId}`
      );

      let pricesRes;
      try {
        pricesRes = await axiosInstance.get(`/Price/hotel/${hotelId}`);
      } catch (error) {
        if (error.response?.status === 404) pricesRes = { data: [] };
        else throw error;
      }

      const rt = Array.isArray(roomTypesRes.data) ? roomTypesRes.data : [];
      const pr = Array.isArray(pricesRes.data) ? pricesRes.data : [];

      setSetupStatus({
        hasRoomTypes: rt.length > 0 && rt.some((x) => (x?.roomTypeId ?? 0) > 0),
        hasPrices: pr.length > 0,
      });
    } catch (error) {
      console.error("Error checking prerequisites:", error);
      setSetupStatus({ hasRoomTypes: false, hasPrices: false });
      setSetupError(error?.message || "Failed to check prerequisites.");
    } finally {
      setSetupLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    fetchRoomTypes();
    fetchRooms();
    checkPrerequisites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const getStatusString = (status) => {
    const statusMap = {
      Available: "Available",
      Occupied: "Occupied",
      Maintenance: "Maintenance",
      Reserved: "Reserved",
      UnderMaintenance: "UnderMaintenance",
      Cleaning: "Cleaning",
      Dirty: "Dirty",
      OutOfService: "OutOfService",
      Unknown: "Unknown",
      0: "Available",
      1: "Occupied",
      2: "Maintenance",
      3: "Reserved",
      4: "UnderMaintenance",
      5: "Cleaning",
      6: "Dirty",
      7: "OutOfService",
      9: "Unknown",
    };
    return statusMap[status] || "Unknown";
  };

  const normalizeQrToDataUrl = (rawQR) => {
    if (!rawQR) return null;
    const v = String(rawQR);
    return v.startsWith("data:image") ? v : `data:image/png;base64,${v}`;
  };

  const fetchRooms = useCallback(async () => {
    if (!hotelId) return;

    setLoading(true);
    try {
      const res = await axiosInstance.get(`/Rooms/all/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];

      const hotelPrices = await fetchPrices();

      const roomsWithPrices = list.map((room) => {
        const roomTypeId = room?.roomTypeId ?? room?.RoomTypeId;
        const roomPrice = hotelPrices.find((p) => p?.roomTypeId === roomTypeId);

        const resolvedTypeName =
          room?.roomTypeName ||
          room?.roomType?.typeName ||
          roomTypes.find((rt) => rt?.roomTypeId === roomTypeId)?.typeName ||
          "Unknown";

        const rawQR = room?.qrCodeBase64 || room?.QRCodeBase64 || null;

        return {
          ...room,
          status: getStatusString(room?.status),
          roomTypeName: resolvedTypeName,
          typeName: resolvedTypeName,
          qrCodeBase64: normalizeQrToDataUrl(rawQR),
          price: roomPrice?.basePrice || 0,
          currency: roomPrice?.currency || "BWP",
          vatPercentage: roomPrice?.vatPercentage || 0,
          additionalLevies: roomPrice?.additionalLevies || 0,
          discount: roomPrice?.discount || 0,
        };
      });

      setRooms(roomsWithPrices);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      notify.error({
        title: "Error",
        description: "Failed to load rooms.",
      });
      setRooms([]);
    } finally {
      setLoading(false);
    }
  }, [hotelId, roomTypes, fetchPrices]);

  const renderQRCode = (qrCodeBase64) => {
    if (!qrCodeBase64) return <span className="hms-muted">No QR</span>;
    return (
      <Popover content={<Image src={qrCodeBase64} width={160} />} trigger="hover">
        <Button
          type="text"
          className="hms-row-action"
          aria-label="Show QR code"
          icon={<QrcodeOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Popover>
    );
  };

  const formatMoney = (ccy, amount) => fmtMoney(ccy || "BWP", Number(amount ?? 0));

  const calcFinalPrice = (price, record) => {
    const base = Number(price ?? 0);
    const fees = Number(record?.additionalLevies ?? 0);
    const vatPct = Number(record?.vatPercentage ?? 0);
    const discountPct = Number(record?.discount ?? 0);

    const totalBeforeVat = base + fees;
    const vatAmount = (totalBeforeVat * vatPct) / 100;
    const final = totalBeforeVat + vatAmount;
    const discounted = discountPct > 0 ? final * (1 - discountPct / 100) : final;

    return { base, fees, vatPct, vatAmount, final, discountPct, discounted };
  };

  const handleDelete = useCallback(
    async (roomId) => {
      try {
        await axiosInstance.delete(`/Rooms/${roomId}`);
        notify.success({
          title: "Success",
          description: "Room deleted successfully",
        });
        fetchRooms();
      } catch {
        notify.error({
          title: "Error",
          description: "Failed to delete room",
        });
      }
    },
    [fetchRooms]
  );

  const handleBatchDelete = useCallback(() => {
    confirm({
      title: `Delete ${selectedRowKeys.length} room${selectedRowKeys.length === 1 ? "" : "s"}?`,
      content: "This cannot be undone.",
      centered: true,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) => axiosInstance.delete(`/Rooms/${id}`))
          );
          notify.success({
            title: "Success",
            description: `${selectedRowKeys.length} rooms deleted successfully`,
          });
          setSelectedRowKeys([]);
          fetchRooms();
        } catch {
          notify.error({
            title: "Error",
            description: "Failed to delete rooms",
          });
        }
      },
    });
  }, [selectedRowKeys, fetchRooms]);

  const handleViewRoom = useCallback((room) => {
    setCurrentRoom(room);
    setEditModalOpen(false);
    setViewDrawerOpen(true);
  }, []);

  const handleEditRoom = useCallback((room) => {
    setCurrentRoom(room);
    setViewDrawerOpen(false);
    setEditModalOpen(true);
  }, []);

  const handleModalEditSuccess = useCallback(() => {
    setEditModalOpen(false);
    fetchRooms();
    notify.success({
      title: "Success",
      description: "Room updated successfully",
    });
  }, [fetchRooms]);

  const hasSelected = selectedRowKeys.length > 0;

  const statusOptions = useMemo(() => {
    const seen = Array.from(new Set(rooms.map((r) => r?.status || "Unknown")));
    return [{ value: "all", label: "All statuses" }, ...seen.map((s) => ({ value: s, label: s }))];
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rooms.filter((r) => {
      if (statusFilter !== "all" && (r?.status || "Unknown") !== statusFilter) return false;
      if (!q) return true;
      const typeName = r?.roomTypeName || r?.typeName || r?.roomType?.typeName || "";
      return String(r?.roomNumber ?? "").toLowerCase().includes(q) || String(typeName).toLowerCase().includes(q);
    });
  }, [rooms, searchText, statusFilter]);

  const columns = useMemo(() => {
    return [
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 140,
        render: (text, record) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{text || "—"}</span>
            <span className="hms-cell-sub">#{record?.roomId}</span>
          </div>
        ),
      },
      {
        title: "Type",
        key: "type",
        width: 200,
        render: (_, record) =>
          record?.roomTypeName || record?.typeName || record?.roomType?.typeName || "Unknown",
      },
      {
        title: "Status",
        key: "status",
        width: 160,
        render: (_, record) => <StatusTag status={record?.status || "Unknown"}>{record?.status || "Unknown"}</StatusTag>,
      },
      {
        title: "Price / night",
        dataIndex: "price",
        key: "price",
        width: 200,
        align: "right",
        render: (price, record) => {
          if (Number(price ?? 0) <= 0) return <StatusTag status="cancelled">Price not set</StatusTag>;

          const ccy = record?.currency || "BWP";
          const p = calcFinalPrice(price, record);

          return (
            <Popover
              title="Price breakdown"
              trigger="hover"
              content={
                <div style={{ minWidth: 240 }}>
                  <DetailList
                    items={[
                      { label: "Base", value: formatMoney(ccy, p.base) },
                      { label: "Fees", value: formatMoney(ccy, p.fees) },
                      p.vatPct > 0 ? { label: `VAT (${p.vatPct}%)`, value: formatMoney(ccy, p.vatAmount) } : null,
                      p.discountPct > 0
                        ? { label: `Discount (${p.discountPct}%)`, value: `-${formatMoney(ccy, p.final - p.discounted)}` }
                        : null,
                      { label: "Total", value: <strong>{formatMoney(ccy, p.discounted)}</strong> },
                    ]}
                  />
                </div>
              }
            >
              <div className="hms-cell-stack" style={{ cursor: "pointer", alignItems: "flex-end" }}>
                <span className="hms-cell-main">{formatMoney(ccy, p.discounted)}</span>
                {p.discountPct > 0 && <span className="hms-cell-sub">{p.discountPct}% discount</span>}
              </div>
            </Popover>
          );
        },
      },
      {
        title: "QR",
        key: "qrCode",
        width: 90,
        align: "center",
        render: (_, record) => renderQRCode(record?.qrCodeBase64),
      },
      {
        title: "",
        key: "action",
        fixed: "right",
        align: "right",
        width: 150,
        render: (_, record) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RowActions
              actions={[
                { key: "view", label: "View room", icon: "view", onClick: () => handleViewRoom(record) },
                { key: "edit", label: "Edit room", icon: "edit", onClick: () => handleEditRoom(record) },
                {
                  key: "delete",
                  label: "Delete room",
                  icon: "delete",
                  danger: true,
                  confirm: "Delete this room?",
                  okText: "Delete",
                  onClick: () => handleDelete(record?.roomId),
                },
              ]}
            />
          </span>
        ),
      },
    ];
  }, [handleViewRoom, handleEditRoom, handleDelete]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
    }),
    [selectedRowKeys]
  );

  const canAddRoom = !setupLoading && setupStatus.hasRoomTypes && setupStatus.hasPrices;
  const refreshAll = () => {
    fetchRooms();
    fetchRoomTypes();
    checkPrerequisites();
  };

  return (
    <div className="hms-page rooms-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfoPopup(true)}>
              Guide
            </Button>
            <Button
              type="primary"
              icon={<IconPlus />}
              onClick={() => setAddModalOpen(true)}
              disabled={!canAddRoom}
              loading={setupLoading}
            >
              Add room
            </Button>
          </>
        }
      />

      {!setupLoading && !setupStatus.hasRoomTypes && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              No room types configured, or they failed to load. Please{" "}
              <Link to="/hotels/roomtypespage">create room types</Link> first.
              {setupError && <div className="hms-muted">Error: {setupError}</div>}
            </span>
          }
        />
      )}

      {setupStatus.hasRoomTypes && !setupStatus.hasPrices && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              Room types exist but no prices are configured. Please <Link to="/hotels/roompricepage">set prices</Link>.
            </span>
          }
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={rooms.length}>Rooms</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchText, onChange: setSearchText, placeholder: "Search room or type" }}
            onRefresh={refreshAll}
            loading={loading}
          >
            {hasSelected && (
              <Button danger icon={ACTION_ICONS.delete} onClick={handleBatchDelete}>
                Delete selected ({selectedRowKeys.length})
              </Button>
            )}
            <Select style={{ width: 170 }} value={statusFilter} onChange={setStatusFilter} options={statusOptions} />
          </ListToolbar>
        }
      >
        <Table
          className="hms-table"
          rowKey="roomId"
          rowSelection={{ type: "checkbox", ...rowSelection }}
          columns={columns}
          dataSource={filteredRooms}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={tablePagination("room")}
          onRow={(record) => ({ onClick: () => handleViewRoom(record), style: { cursor: "pointer" } })}
          locale={{
            emptyText: (
              <div className="hms-empty">
                No rooms found.{" "}
                {canAddRoom && (
                  <Button type="link" onClick={() => setAddModalOpen(true)}>
                    Add a room
                  </Button>
                )}
              </div>
            ),
          }}
        />
      </SectionCard>

      {addModalOpen && (
        <AddRoomModal
          open={addModalOpen}
          onCancel={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false);
            fetchRooms();
          }}
          roomTypes={roomTypes}
        />
      )}

      {currentRoom && (
        <>
          <RoomDetailDrawer open={viewDrawerOpen} room={currentRoom} onClose={() => setViewDrawerOpen(false)} />

          <RoomEditModal
            open={editModalOpen}
            room={currentRoom}
            onCancel={() => setEditModalOpen(false)}
            onSuccess={handleModalEditSuccess}
            roomTypes={roomTypes}
          />
        </>
      )}

      <Modal
        title="Room management guide"
        open={showInfoPopup}
        onCancel={() => setShowInfoPopup(false)}
        width={560}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoPopup(false)}>
            Got it
          </Button>,
        ]}
      >
        <p className="hms-modal-note">Manage every room in your hotel, its price visibility and its QR code.</p>

        <Section title="Set up in this order">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Room Types: define categories (for example Standard, Deluxe).</li>
            <li>Room Price: set the rate for each room type.</li>
            <li>Room List: add individual rooms with a number and a type.</li>
          </ol>
        </Section>

        <Section title="Your setup">
          <DetailList
            items={[
              {
                label: "Room types",
                value: (
                  <StatusTag status={setupStatus.hasRoomTypes ? "available" : "cancelled"}>
                    {setupStatus.hasRoomTypes ? "Configured" : "Missing"}
                  </StatusTag>
                ),
              },
              {
                label: "Prices",
                value: (
                  <StatusTag status={setupStatus.hasPrices ? "available" : "cancelled"}>
                    {setupStatus.hasPrices ? "Configured" : "Missing"}
                  </StatusTag>
                ),
              },
            ]}
          />
        </Section>

        <p className="hms-modal-note" style={{ marginTop: 16 }}>
          A QR code is generated for every room; scan it for quick access.
        </p>
      </Modal>
    </div>
  );
};

export default RoomListPage;
